import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import UserProfileModal from "../components/UserProfileModal";
import GuestProfileModal from "../components/GuestProfileModal";
import { 
  ArrowLeft, 
  Trophy, 
  TrendingUp, 
  Calendar, 
  Sword,
  Crown,
  Medal,
  Flame,
  Clock,
  User
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Boss image mapping
const BOSS_IMAGE_MAPPING = {
  "Mano": "https://legends.ml/static/images/lib/monster/2220000.png",
  "Stumpy": "https://legends.ml/static/images/lib/monster/3220000.png",
  "Deo": "https://legends.ml/static/images/lib/monster/3220001.png",
  "King Clang": "https://legends.ml/static/images/lib/monster/5220000.png",
  "Seruf": "https://legends.ml/static/images/lib/monster/4220001.png",
  "Faust": "https://legends.ml/static/images/lib/monster/5220002.png",
  "Giant Centipede": "https://legends.ml/static/images/lib/monster/9500177.png",
  "Timer": "https://legends.ml/static/images/lib/monster/5220003.png",
  "Mushmom": "https://legends.ml/static/images/lib/monster/6130101.png",
  "Dyle": "https://legends.ml/static/images/lib/monster/6220000.png",
  "Zombie Mushmom": "https://legends.ml/static/images/lib/monster/6300005.png",
  "Zeno": "https://legends.ml/static/images/lib/monster/6220001.png",
  "Nine-Tailed Fox": "https://legends.ml/static/images/lib/monster/7220001.png",
  "Tae Roon": "https://legends.ml/static/images/lib/monster/7220000.png",
  "King Sage Cat": "https://legends.ml/static/images/lib/monster/7220002.png",
  "Jr. Balrog": "https://legends.ml/static/images/lib/monster/8130100.png",
  "Eliza": "https://legends.ml/static/images/lib/monster/8220000.png",
  "Snack Bar": "https://legends.ml/static/images/lib/monster/9410015.png",
  "Chimera": "https://legends.ml/static/images/lib/monster/8220002.png",
  "Blue Mushmom": "https://legends.ml/static/images/lib/monster/9400205.png",
  "Snowman": "https://legends.ml/static/images/lib/monster/8220001.png",
  "Headless Horseman": "https://legends.ml/static/images/lib/monster/9400549.png",
  "Manon": "https://legends.ml/static/images/lib/monster/9500174.png",
  "Griffey": "https://legends.ml/static/images/lib/monster/8180001.png",
  "Pianus (16h)": "https://legends.ml/static/images/lib/monster/8510000.png",
  "Pianus (24h)": "https://legends.ml/static/images/lib/monster/8520000.png",
  "BigFoot": "https://legends.ml/static/images/lib/monster/9400575.png",
  "Black Crow": "https://legends.ml/static/images/lib/monster/9400014.png",
  "Leviathan": "https://legends.ml/static/images/lib/monster/8220003.png",
  "Kacchuu Musha": "https://legends.ml/static/images/lib/monster/9400405.png",
  "Dodo": "https://legends.ml/static/images/lib/monster/8220004.png",
  "Anego / Female Boss": "https://legends.ml/static/images/lib/monster/9400121.png",
  "Lilynouch": "https://legends.ml/static/images/lib/monster/8220005.png",
  "Lyka": "https://legends.ml/static/images/lib/monster/8220006.png",
  "Balrog": "https://royals-library.netlify.app/images/monsters/8830000.png"
};

const getBossImage = (bossName) => {
  const baseName = bossName?.split(" - ")[0];
  return BOSS_IMAGE_MAPPING[baseName] || "https://legends.ml/static/images/lib/monster/6130101.png";
};

const GlobalStatsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState("today");
  
  // User Profile Modal
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [selectedUsername, setSelectedUsername] = useState(null);
  
  // Guest Profile Modal (for admins)
  const [guestProfileModalOpen, setGuestProfileModalOpen] = useState(false);
  const [selectedGuestAlias, setSelectedGuestAlias] = useState(null);
  
  // Check if user is admin
  const isAdmin = user?.is_admin || user?.is_junior_admin;

  useEffect(() => {
    fetchGlobalStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const fetchGlobalStats = async () => {
    setLoading(true);
    
    try {
      // Always include anonymous data (include_anonymous=true)
      const response = await axios.get(`${API_URL}/api/stats/global?period=${period}&include_anonymous=true`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch global stats:", error);
      toast.error("Failed to load global statistics");
    } finally {
      setLoading(false);
    }
  };

  const viewUserProfile = (username) => {
    if (username === "Anonymous") return;
    // Check if it's a guest alias (Anonymous_*)
    if (username.startsWith("Anonymous_")) {
      if (isAdmin) {
        setSelectedGuestAlias(username);
        setGuestProfileModalOpen(true);
      }
      return;
    }
    setSelectedUsername(username);
    setProfileModalOpen(true);
  };

  const getPeriodLabel = () => {
    switch (period) {
      case "today": return "Today";
      case "week": return "This Week";
      case "month": return "This Month";
      case "all": return "All Time";
      default: return "Today";
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading global statistics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="global-stats-page">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Global Statistics</h1>
              <p className="text-sm text-muted-foreground">All hunters combined</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Period Selector */}
        <Card className="bg-card/50">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-2">
              {[
                { value: "today", label: "Today", icon: Sword, color: "red" },
                { value: "week", label: "Week", icon: TrendingUp, color: "emerald" },
                { value: "month", label: "Month", icon: Calendar, color: "amber" },
                { value: "all", label: "All Time", icon: Trophy, color: "purple" },
              ].map((option) => (
                <Button
                  key={option.value}
                  size="sm"
                  variant={period === option.value ? "default" : "outline"}
                  onClick={() => setPeriod(option.value)}
                  className="gap-2"
                >
                  <option.icon className="w-4 h-4" />
                  {option.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Summary Stats - Only Total Kills */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <Sword className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{stats?.total_kills || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Kills ({getPeriodLabel()})</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <Flame className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{stats?.unique_bosses || 0}</p>
                  <p className="text-sm text-muted-foreground">Different Bosses Hunted</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* All Hunted Bosses */}
        <Card className="bg-card/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-red-400" />
              All Hunted Bosses ({getPeriodLabel()})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : !stats?.kills_by_boss || Object.keys(stats.kills_by_boss).length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No data available</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(stats.kills_by_boss)
                  .sort(([, a], [, b]) => b - a)
                  .map(([boss, count], idx) => {
                    const maxKills = Math.max(...Object.values(stats.kills_by_boss));
                    const percentage = (count / maxKills) * 100;
                    
                    return (
                      <div 
                        key={boss}
                        className="flex items-center gap-3 p-3 rounded-lg bg-background/50 border border-border/50"
                      >
                        {/* Rank */}
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                          idx === 0 ? "bg-yellow-500/20 text-yellow-400" :
                          idx === 1 ? "bg-slate-400/20 text-slate-300" :
                          idx === 2 ? "bg-amber-700/20 text-amber-600" :
                          "bg-muted text-muted-foreground"
                        }`}>
                          {idx + 1}
                        </span>

                        {/* Boss Image */}
                        <div className="w-10 h-10 bg-background rounded flex-shrink-0 flex items-center justify-center overflow-hidden">
                          <img
                            src={getBossImage(boss)}
                            alt={boss}
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>

                        {/* Boss Name + Bar */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{boss.split(" - ")[0]}</p>
                          <div className="mt-1 h-1.5 bg-background rounded overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-red-500/60 to-red-400 rounded transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>

                        {/* Kill Count */}
                        <span className="text-sm font-bold text-primary">{count}</span>
                      </div>
                    );
                  })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Global Kills */}
        {stats?.recent_kills && stats.recent_kills.length > 0 && (
          <Card className="bg-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Recent Kills
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {stats.recent_kills.map((kill, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 p-3 rounded-lg bg-background/50 border border-border/50 hover:bg-background/80 transition-colors"
                    >
                      {/* Boss Image */}
                      <img
                        src={getBossImage(kill.boss_name)}
                        alt={kill.boss_name}
                        className="w-10 h-10 object-contain flex-shrink-0"
                      />
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{kill.boss_name.split(" - ")[0]}</p>
                        <p className="text-xs text-muted-foreground">
                          Channel {kill.channel + 1}
                        </p>
                      </div>

                      {/* Killer Name - Prominent */}
                      <div className="text-right">
                        {kill.killer && kill.killer !== "Anonymous" ? (
                          kill.killer.startsWith("Anonymous_") ? (
                            // Guest with ore alias
                            isAdmin ? (
                              <button
                                onClick={() => viewUserProfile(kill.killer)}
                                className="text-sm font-semibold text-amber-400 hover:underline"
                              >
                                {kill.killer}
                              </button>
                            ) : (
                              <span className="text-sm text-amber-400">{kill.killer}</span>
                            )
                          ) : (
                            // Registered user
                            <button
                              onClick={() => viewUserProfile(kill.killer)}
                              className="text-sm font-semibold text-primary hover:underline"
                            >
                              {kill.killer}
                            </button>
                          )
                        ) : (
                          <span className="text-sm text-muted-foreground">Anonymous</span>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {formatTime(kill.killed_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}
      </div>

      {/* User Profile Modal */}
      <UserProfileModal
        open={profileModalOpen}
        onOpenChange={setProfileModalOpen}
        username={selectedUsername}
      />
      
      {/* Guest Profile Modal (Admin only) */}
      <GuestProfileModal
        isOpen={guestProfileModalOpen}
        onClose={() => setGuestProfileModalOpen(false)}
        guestAlias={selectedGuestAlias}
      />
    </div>
  );
};

export default GlobalStatsPage;
